import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inquiryTypes = [
  { value: "wealth-advisory", label: "Wealth Advisory / Financial Services" },
  { value: "hr-talent", label: "HR & Talent Services" },
  { value: "fintech", label: "Fintech" },
  { value: "professional-services", label: "Professional Services" },
  { value: "creative-enterprise", label: "Enterprise Creative / Video" },
  { value: "other", label: "Other Industry" }
];

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    inquiry_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, inquiry_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.inquiry_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you for reaching out. We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        company: "",
        inquiry_type: "",
        message: ""
      });
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-[#0A0A0A] border border-white/10 text-white max-w-md p-0 overflow-hidden rounded-none max-h-[90vh] overflow-y-auto"
        data-testid="contact-modal"
      >
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-medium text-white mb-1">
            Start a conversation
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            Tell us about your business. We'll reach out to schedule a call.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-6 pt-2 space-y-4">
          <div>
            <label htmlFor="name" className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
              Full Name <span className="text-[#6DBE2E]">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              data-testid="contact-input-name"
              className="w-full bg-transparent border-0 border-b border-white/15 text-white text-sm py-2 px-0 focus:outline-none focus:border-[#6DBE2E] transition-colors placeholder:text-white/25"
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="email" className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
              Work Email <span className="text-[#6DBE2E]">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              data-testid="contact-input-email"
              className="w-full bg-transparent border-0 border-b border-white/15 text-white text-sm py-2 px-0 focus:outline-none focus:border-[#6DBE2E] transition-colors placeholder:text-white/25"
              placeholder="you@company.com"
            />
          </div>

          <div>
            <label htmlFor="company" className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
              Company <span className="text-[#6DBE2E]">*</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              data-testid="contact-input-company"
              className="w-full bg-transparent border-0 border-b border-white/15 text-white text-sm py-2 px-0 focus:outline-none focus:border-[#6DBE2E] transition-colors placeholder:text-white/25"
              placeholder="Your company name"
            />
          </div>

          <div>
            <label htmlFor="inquiry_type" className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
              Industry <span className="text-[#6DBE2E]">*</span>
            </label>
            <Select value={formData.inquiry_type} onValueChange={handleSelectChange}>
              <SelectTrigger 
                data-testid="contact-input-inquiry-type"
                className="w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-2 text-white text-sm focus:ring-0 focus:border-[#6DBE2E] transition-colors h-auto"
              >
                <SelectValue placeholder="Select your industry" className="text-white/25" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border border-white/10 rounded-none">
                {inquiryTypes.map((type) => (
                  <SelectItem 
                    key={type.value} 
                    value={type.value}
                    className="text-white/80 hover:bg-white/5 focus:bg-white/5 focus:text-white text-sm"
                  >
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label htmlFor="message" className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
              Additional Context <span className="text-white/25">(Optional)</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              data-testid="contact-input-message"
              rows={2}
              className="w-full bg-transparent border-0 border-b border-white/15 text-white text-sm py-2 px-0 focus:outline-none focus:border-[#6DBE2E] transition-colors resize-none placeholder:text-white/25"
              placeholder="What are you looking to achieve?"
            />
          </div>

          <div className="pt-1">
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
              className="w-full bg-[#6DBE2E] text-black hover:bg-[#5CAD25] rounded-none font-semibold py-4 text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Request
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>

          <p className="text-white/25 text-xs text-center pt-1">
            Your information is used only to respond to your inquiry.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
